import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import adminApi from '../utils/adminApi';

const PAGE_LABELS = {
  '/': { name: 'Home', icon: 'fa-house', color: 'bg-sky-500' },
  '/contact': { name: 'Contact', icon: 'fa-envelope', color: 'bg-purple-500' },
  '/shop': { name: 'Shop', icon: 'fa-store', color: 'bg-emerald-500' },
  '/print': { name: 'Print Services', icon: 'fa-print', color: 'bg-orange-500' },
  '/app': { name: 'Mobile App', icon: 'fa-mobile-screen', color: 'bg-indigo-500' },
  '/server': { name: 'Server Health', icon: 'fa-heart-pulse', color: 'bg-red-500' },
};

const pageInfo = (path) => PAGE_LABELS[path] || { name: path, icon: 'fa-link', color: 'bg-slate-500' };

const Analytics = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get('/analytics/today');
      setData(r.data);
    } catch { toast.error('Could not load analytics'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); const id = setInterval(load, 60 * 1000); return () => clearInterval(id); }, [load]);

  const maxTop = Math.max(1, ...(data?.top_pages || []).map((p) => p.views));
  const maxHour = Math.max(1, ...(data?.hourly || []));
  const delta = data ? data.total_views - data.yesterday_views : 0;
  const deltaUp = delta >= 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/staff')} className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all shadow-md border border-gray-200" data-testid="back-to-admin-btn">
              <i className="fas fa-arrow-left"></i>
              <span>Back to Admin</span>
            </button>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-indigo-600">Traffic</p>
              <h1 className="text-2xl font-black text-slate-900 tracking-tight">Today&apos;s Analytics</h1>
              <p className="text-xs text-slate-500 mt-0.5">Auto-refreshes every minute · Data kept for 30 days</p>
            </div>
          </div>
          <button onClick={load} className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition-all shadow-sm border border-slate-200 text-sm" data-testid="refresh-btn">
            <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>
            <span>Refresh</span>
          </button>
        </div>

        {loading && !data ? (
          <div className="bg-white rounded-2xl shadow-md p-12 text-center text-slate-400">
            <i className="fas fa-spinner fa-spin text-3xl mb-2"></i>
            <p>Loading analytics...</p>
          </div>
        ) : data ? (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-gradient-to-br from-sky-500 to-blue-600 text-white rounded-2xl p-5 shadow-lg" data-testid="kpi-visitors">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                    <i className="fas fa-users text-white"></i>
                  </div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/80">Unique visitors</p>
                </div>
                <p className="text-4xl font-black tabular-nums">{data.unique_visitors}</p>
                <p className="text-xs text-white/70 mt-1">Distinct browser sessions today</p>
              </div>

              <div className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-2xl p-5 shadow-lg" data-testid="kpi-views">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                    <i className="fas fa-eye text-white"></i>
                  </div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/80">Total page views</p>
                </div>
                <p className="text-4xl font-black tabular-nums">{data.total_views}</p>
                <p className="text-xs text-white/70 mt-1 flex items-center gap-1">
                  <i className={`fas fa-arrow-${deltaUp ? 'up' : 'down'}`}></i>
                  {Math.abs(delta)} vs yesterday same time
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-500 to-fuchsia-600 text-white rounded-2xl p-5 shadow-lg" data-testid="kpi-top-page">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                    <i className="fas fa-trophy text-white"></i>
                  </div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/80">Most-viewed page</p>
                </div>
                {data.top_pages[0] ? (
                  <>
                    <p className="text-2xl font-black leading-tight">{pageInfo(data.top_pages[0].path).name}</p>
                    <p className="text-xs text-white/70 mt-1">{data.top_pages[0].views} views today</p>
                  </>
                ) : (
                  <p className="text-white/60 italic">No views yet today</p>
                )}
              </div>
            </div>

            {/* Top Pages */}
            <div className="grid grid-cols-1 md:grid-cols-[1.2fr_1fr] gap-4">
              <div className="bg-white rounded-2xl shadow-md border border-slate-200 p-5" data-testid="top-pages-panel">
                <div className="flex items-center gap-2 mb-4">
                  <i className="fas fa-list-ol text-indigo-600"></i>
                  <h2 className="font-black text-slate-900">Top pages today</h2>
                </div>
                {data.top_pages.length === 0 ? (
                  <p className="text-sm text-slate-400 italic py-6 text-center" data-testid="no-pages">No views yet today. As soon as customers visit the site, they will appear here.</p>
                ) : (
                  <div className="space-y-3">
                    {data.top_pages.map((p, i) => {
                      const info = pageInfo(p.path);
                      const pct = Math.round((p.views / maxTop) * 100);
                      return (
                        <div key={p.path} className="flex items-center gap-3" data-testid={`page-row-${i}`}>
                          <div className={`w-8 h-8 rounded-lg ${info.color} flex items-center justify-center text-white text-xs`}>
                            <i className={`fas ${info.icon}`}></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between text-sm">
                              <p className="font-bold text-slate-800 truncate">{info.name}</p>
                              <span className="text-slate-500 tabular-nums font-mono ml-2">{p.views}</span>
                            </div>
                            <div className="mt-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div className={`h-full ${info.color} rounded-full transition-all`} style={{ width: `${pct}%` }}></div>
                            </div>
                            <p className="text-[10px] text-slate-400 font-mono mt-0.5">{p.path}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Hourly chart */}
              <div className="bg-white rounded-2xl shadow-md border border-slate-200 p-5" data-testid="hourly-panel">
                <div className="flex items-center gap-2 mb-4">
                  <i className="fas fa-chart-column text-indigo-600"></i>
                  <h2 className="font-black text-slate-900">Views by hour</h2>
                </div>
                <div className="flex items-end justify-between gap-0.5 h-40" data-testid="hourly-chart">
                  {data.hourly.map((v, h) => {
                    const pct = Math.round((v / maxHour) * 100);
                    return (
                      <div key={h} className="flex-1 flex flex-col items-center justify-end h-full">
                        <span className="text-[9px] text-slate-400 font-mono">{v || ''}</span>
                        <div
                          className={`w-full rounded-t ${v > 0 ? 'bg-gradient-to-t from-indigo-500 to-purple-500' : 'bg-slate-100'}`}
                          style={{ height: `${Math.max(pct, 3)}%` }}
                          title={`${h}:00 — ${v} views`}
                        ></div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between mt-2 text-[9px] font-mono text-slate-400 uppercase tracking-widest">
                  <span>00</span><span>06</span><span>12</span><span>18</span><span>23</span>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="bg-white rounded-2xl shadow-md p-12 text-center text-slate-400">
            <i className="fas fa-triangle-exclamation text-3xl mb-2"></i>
            <p>Could not load analytics data.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Analytics;
